import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import * as moment from 'moment';
import translate from '../../../../commonComponents/translate';
import {
  formatDate,
  formatNumbers,
  newFormatPrice,
} from '../../../../services/utilityFunctions';
import LoggedUserContext from '../../../../contextProviders/LoggedUserContext';
import {
  formatDecimalPrices,
  isSmallerThanTwoDecimal,
  normalizeNumber,
} from '../../../../services/numberUtilis';
import OrganizationDirectSelling from '../../../../commonComponents/propTypes/OrganizationDirectSelling';
import CommissionTotalsItem from './CommissionTotalsItem';

function CommissionTotals(props) {
  const { t, directSellingSettings, totals, filters, summary } = props;

  const {
    teamVA: summaryTeamVA,
    VALeft: summaryVALeft,
    VARight: summaryVARight,
    remainingBinaryVA: summaryRemainingBinaryVA,
  } = summary;

  const { loggedUser } = useContext(LoggedUserContext);
  const { currency = loggedUser.currency } = totals;

  const formatMoney = (value, hasHint = false) => {
    const result = isSmallerThanTwoDecimal(value || 0)
      ? `${formatDecimalPrices(value || 0, true, currency)}`
      : newFormatPrice(value || 0, true, currency, true);
    return hasHint ? `${result}*` : result;
  };

  const { VAIntegration, salesIntegration, binaryIntegration, recurrence } =
    directSellingSettings || {};

  const isRecurrenceWeekly =
    recurrence && recurrence === OrganizationDirectSelling.RECURRENCE.WEEKLY;

  const {
    userSales = 0,
    totalCommissions = 0,
    userVA = 0,
    groupVA: groupVATotal = 0,
    VALeft: VALeftTotal = 0,
    VARight: VARightTotal = 0,
    remainingBinaryVA: remainingBinaryVATotal = 0,
    userCommission = 0,
    groupCommissions = 0,
    forecastDate: totalsForecastDate,
  } = totals;

  const { sponsor, startDate } = filters;

  // Block Values with Sponsor
  const personalSales = formatMoney(userSales);
  const totalCommissionsForSponsor = formatMoney(totalCommissions);
  const personalSponsorCommission = formatMoney(userCommission);

  const personalVA = normalizeNumber(userVA || 0, 2);

  // Used to show the blocks from forecast, with different hints and/or color
  const todayDate = new Date();
  const isThisMonth = new Date(startDate).getMonth() === todayDate.getMonth();

  const weekOfToday = moment(todayDate).isoWeek();
  const weekOfStartDate = moment(startDate).isoWeek();
  const isThisWeek = weekOfToday === weekOfStartDate;

  const isForecast =
    (recurrence &&
      recurrence === OrganizationDirectSelling.RECURRENCE.WEEKLY &&
      isThisWeek) ||
    ((!recurrence ||
      recurrence === OrganizationDirectSelling.RECURRENCE.MONTHLY) &&
      isThisMonth);
  const isNotFromReport = !isRecurrenceWeekly
    ? isThisMonth && sponsor
    : isThisWeek && sponsor;
  let groupVA = summaryTeamVA ? normalizeNumber(summaryTeamVA || 0, 2) : '-';
  const forecastDate = totalsForecastDate
    ? formatDate(totalsForecastDate, 'DD MMM YYYY')
    : null;
  if (isNotFromReport) {
    groupVA =
      groupVATotal && groupVATotal >= 0
        ? `${normalizeNumber(groupVATotal || 0)}*`
        : '-';
  }

  let totalGroupCommissions = '-';
  if (isForecast && groupCommissions) {
    totalGroupCommissions = formatMoney(groupCommissions, true);
  }

  let VALeft = summaryVALeft ? normalizeNumber(summaryVALeft || 0, 2) : 0;
  if (isNotFromReport) {
    VALeft =
      VALeftTotal && VALeftTotal >= 0
        ? `${normalizeNumber(VALeftTotal || 0, 2)}*`
        : 0;
  }

  let VARight = summaryVARight ? normalizeNumber(summaryVARight || 0, 2) : 0;

  if (isNotFromReport) {
    VARight =
      VARightTotal && VARightTotal >= 0
        ? `${normalizeNumber(VARightTotal || 0, 2)}*`
        : 0;
  }

  let remainingBinaryVA = summaryRemainingBinaryVA
    ? normalizeNumber(summaryRemainingBinaryVA || 0, 2)
    : 0;
  if (isNotFromReport) {
    remainingBinaryVA =
      remainingBinaryVATotal && remainingBinaryVATotal >= 0
        ? `${normalizeNumber(remainingBinaryVATotal || 0, 2)}*`
        : 0;
  }

  const forecastHint =
    forecastDate && isForecast
      ? `*${t('commissions.teamVA_description.forecast.hint', forecastDate)}`
      : '';

  // Allow us to get a responsive block view, no matter what blocks are active
  let sponsorBlocksActive = 1;
  if (salesIntegration) {
    sponsorBlocksActive += 2;
  } else {
    sponsorBlocksActive += 1;
  }
  if (VAIntegration) {
    sponsorBlocksActive += 2;
  } else {
    sponsorBlocksActive += 1;
  }

  const personalSalesDescription = VAIntegration
    ? t('commissions.personalSales.with.VAIntegration')
    : t('commissions.personalSales.without.VAIntegration');
  const personalCommissionsDescription = VAIntegration
    ? t('commissions.personalCommission.with.VAIntegration')
    : t('commissions.personalCommission.without.VAIntegration');

  return (
    <div className="commission-totals-wrapper">
      <div
        className="grid-row"
        style={{ gridTemplateColumns: `repeat(${sponsorBlocksActive}, 1fr)` }}
      >
        {/** PERSONAL SALES */}
        {!salesIntegration && (
          <div className="grid-item">
            <CommissionTotalsItem
              title={t('commissions.personalSales')}
              description={personalSalesDescription}
              value={personalSales}
            />
          </div>
        )}
        {/** TOTAL TURNOVER */}
        {salesIntegration && (
          <div className="grid-item">
            <CommissionTotalsItem
              title={t('commissions.totalTurnover')}
              description={t('commissions.totalTurnover.description')}
              value={personalSales}
            />
          </div>
        )}
        {/** TOTAL COMMISSIONS */}
        {salesIntegration && (
          <div className="grid-item">
            <CommissionTotalsItem
              title={t('commissions.block.totalCommissions')}
              description={t('commissions.block.totalCommissions.description')}
              value={totalCommissionsForSponsor}
            />
          </div>
        )}
        {/** PERSONAL COMMISSION */}
        <div className="grid-item">
          <CommissionTotalsItem
            title={t('commissions.personalCommission')}
            description={personalCommissionsDescription}
            value={personalSponsorCommission}
          />
        </div>
        {/** PERSONAL VA */}
        {VAIntegration && (
          <div className="grid-item">
            <CommissionTotalsItem
              title={t('commissions.personalVA')}
              description={t('commissions.personalVA.description')}
              value={personalVA}
            />
          </div>
        )}
        {/** TEAM VA */}
        {VAIntegration && (
          <div className="grid-item">
            <CommissionTotalsItem
              title={t('commissions.groupVA')}
              description={
                isForecast
                  ? t('commissions.groupVA.description.forecast')
                  : t('commissions.groupVA.description')
              }
              value={groupVA}
              hint={forecastHint}
              valueClassName={isForecast ? 'forecast-value-date' : ''}
            />
          </div>
        )}
        {/** GROUP COMMISSIONS */}
        {!VAIntegration && (
          <div className="grid-item">
            <CommissionTotalsItem
              title={t('commissions.groupCommission')}
              description={
                isForecast
                  ? t('commissions.groupCommission.description.forecast')
                  : t('commissions.groupCommission.description')
              }
              value={totalGroupCommissions}
              hint={forecastHint}
              valueClassName={isForecast ? 'forecast-value-date' : ''}
            />
          </div>
        )}
      </div>
      {!!(VAIntegration && binaryIntegration) && (
        <div
          className="grid-row"
          style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}
        >
          <div className="grid-item">
            <CommissionTotalsItem
              title={t('commissions.vaLeft')}
              description={t('commissions.vaLeft.description')}
              value={VALeft}
              hint={forecastHint}
              valueClassName={isForecast ? 'forecast-value-date' : ''}
            />
          </div>
          <div className="grid-item">
            <CommissionTotalsItem
              title={t('commissions.vaRight')}
              description={t('commissions.vaRight.description')}
              value={VARight}
              hint={forecastHint}
              valueClassName={isForecast ? 'forecast-value-date' : ''}
            />
          </div>
          <div className="grid-item">
            <CommissionTotalsItem
              title={t('commissions.remainingBinaryVA')}
              description={t('commissions.remainingBinaryVA.description')}
              value={remainingBinaryVA}
              hint={forecastHint}
              valueClassName={isForecast ? 'forecast-value-date' : ''}
            />
          </div>
        </div>
      )}
    </div>
  );
}

CommissionTotals.propTypes = {
  t: PropTypes.func.isRequired,
  directSellingSettings: PropTypes.shape({
    VAIntegration: PropTypes.bool,
    binaryIntegration: PropTypes.bool,
    salesIntegration: PropTypes.bool,
    recurrence: PropTypes.string,
  }).isRequired,
  totals: PropTypes.shape({
    userVA: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    groupVA: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    VALeft: PropTypes.number,
    VARight: PropTypes.number,
    currency: PropTypes.string.isRequired,
    remainingBinaryVA: PropTypes.number,
    userSales: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    teamSales: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    teamCommission: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    userCommission: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    totalCommissions: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    groupCommissions: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    forecastDate: PropTypes.number,
  }),
  filters: PropTypes.shape({
    sponsor: PropTypes.string,
    startDate: PropTypes.number,
  }),
  summary: PropTypes.shape({
    teamVA: PropTypes.number,
    VALeft: PropTypes.number,
    VARight: PropTypes.number,
    remainingBinaryVA: PropTypes.number,
  }),
};

CommissionTotals.defaultProps = {
  totals: {},
  filters: {},
  summary: {},
};

export default translate(CommissionTotals);
